'use client';

import { useEffect, useState } from 'react';

import i18next, { type Namespace } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import resourcesToBackend from 'i18next-resources-to-backend';
import { useCookies } from 'react-cookie';
// oxlint-disable-next-line no-restricted-imports
import {
  type UseTranslationOptions,
  initReactI18next,
  useTranslation as useTr,
} from 'react-i18next';

import { useLangProvider } from '@/components/core/AppLayout';

import { cookieName, getOptions, languages } from './settings';

const runsOnServerSide = typeof window === 'undefined';

//
i18next
  .use(initReactI18next)
  .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`@/public/locales/${language}/${namespace}.json`)
    )
  )
  .init({
    ...getOptions(),
    lng: undefined, // let detect the language on client side
    detection: {
      order: ['path', 'htmlTag', 'cookie', 'navigator'],
    },
    preload: runsOnServerSide ? languages : [],
  });

export function useAppTranslation<const T extends Namespace>(
  ns: T,
  options?: UseTranslationOptions<undefined>
): ReturnType<typeof useTr<T>> {
  const lng = useLangProvider();
  const [cookies, setCookie] = useCookies([cookieName]);
  const ret = useTr(ns, { ...options, lng: options?.lng || lng });
  const { i18n } = ret;

  const shouldChangeLanguage =
    runsOnServerSide && lng && i18n.resolvedLanguage !== lng;

  const [activeLng, setActiveLng] = useState(i18n.resolvedLanguage);

  // useEffect(() => {
  //   if (shouldChangeLanguage) return; // Only run on client
  //   if (activeLng === i18n.resolvedLanguage) return;
  //   setActiveLng(i18n.resolvedLanguage);
  // }, [activeLng, i18n.resolvedLanguage, shouldChangeLanguage]);

  useEffect(() => {
    if (shouldChangeLanguage) return; // Only run on client
    if (!lng || i18n.resolvedLanguage === lng) return;
    i18n.changeLanguage(lng, () => {
      if (i18n.resolvedLanguage !== lng) return;
      if (activeLng === lng) return;
      setActiveLng(lng);
    });
  }, [lng, i18n, shouldChangeLanguage, activeLng]);

  useEffect(() => {
    if (shouldChangeLanguage) return; // Only run on client
    if (cookies.i18next === lng) return;
    setCookie(cookieName, lng, { path: '/' });
  }, [lng, cookies.i18next, setCookie, shouldChangeLanguage]);

  if (shouldChangeLanguage) {
    i18n.changeLanguage(lng);
  }
  return ret;
}
